import React, { memo, useRef, useState, useEffect } from 'react';
import Breadcrumb from './Breadcrumb';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { Icon } from '../ui/Icon';
import WorkspaceSettingsModal from '../../features/projects/components/WorkspaceSettingsModal';
import styles from './PageHeader.module.css';
import TopProgressBar from '../ui/TopProgressBar';

const SettingsButton = ({ onClick }) => (
  <button
    type="button"
    className={styles.settingsButton}
    onClick={onClick}
    aria-label="Workspace settings"
    title="Workspace settings"
  >
    <Icon name="settings" size={20} />
  </button>
);

/**
 * Top-nav navigation icons → centralized `<Icon>`. Only the three live
 * top-nav items render here (Projects/Library/Vulnerability Management,
 * per PageLayout's navigationItems); the legacy icon-name keys map to
 * their closest semantic glyph, falling back to the dashboard mark.
 */
export const NAV_ICON_NAME = {
  dashboard: 'dashboard',
  library: 'library',
  shield: 'shield',
  checklist: 'list-checks',
};

/**
 * Unified page header component used across all Athena pages
 * Matches the style from New Analysis page (AthenaWorkflowOrchestrator)
 *
 * @param {Array} breadcrumbItems - Array of breadcrumb items { label, path }
 * @param {string} currentPage - Current page name shown in breadcrumb
 * @param {React.ReactNode} rightContent - Content to display on the right side (UserInfo, buttons, etc.)
 * @param {React.ReactNode} beforeBellContent - Content to render immediately to the left of NotificationBell
 * @param {React.ReactNode} statusBar - Optional status bar content (used in New Analysis page)
 * @param {boolean} showNavigation - Whether to show navigation menu in header (default: false, uses sidebar instead)
 * @param {Array} navigationItems - Navigation items to display { id, label, icon }
 * @param {string} activeNavItem - Currently active navigation item ID
 * @param {Function} onNavigation - Callback when navigation item is clicked
 */
const PageHeader = memo(({
  breadcrumbItems = [],
  currentPage,
  rightContent = null,
  beforeBellContent = null,
  statusBar = null,
  showNavigation = false,
  navigationItems = [],
  activeNavItem = null,
  onNavigation = null,
}) => {
  const headerRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    const scrollContainer = headerRef.current?.parentElement;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolled(scrollContainer.scrollTop > 0);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Icon mapping for navigation items → centralized `<Icon>`.
  const getNavIcon = (iconName) => (
    <Icon name={NAV_ICON_NAME[iconName] || 'dashboard'} size={20} className={styles.navIcon} />
  );

  return (
    <header
      ref={headerRef}
      className={`${styles.pageHeader} ${isScrolled ? styles.scrolled : ''} ${showNavigation ? styles.withNavigation : ''}`}
    >
      <div className={styles.headerContent}>
        {showNavigation ? (
          <>
            {/* Logo and Navigation Section */}
            <div className={styles.leftSection}>
              <div className={styles.headerLogo}>
                <img src="/athena-logo.png" alt="Athena Logo" className={styles.logoImg} />
                <span className={styles.logoText}>Athena</span>
              </div>

              <nav className={styles.horizontalNav}>
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    className={`${styles.navItem} ${activeNavItem === item.id ? styles.active : ''}`}
                    onClick={() => onNavigation?.(item.id)}
                    title={item.description}
                  >
                    {getNavIcon(item.icon)}
                    <span className={styles.navLabel}>{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Right Section with utilities */}
            <div className={styles.rightSection}>
              {statusBar && (
                <div className={styles.statusBar}>
                  {statusBar}
                </div>
              )}
              <div className={styles.rightContent}>
                {beforeBellContent}
                <ThemeToggle />
                <NotificationBell />
                <SettingsButton onClick={() => setIsSettingsOpen(true)} />
                {rightContent}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Original layout with breadcrumb */}
            <Breadcrumb
              items={breadcrumbItems}
              currentPage={currentPage}
            />

            <div className={styles.rightSection}>
              {statusBar && (
              <div className={styles.statusBar}>
                {statusBar}
              </div>
              )}
              <div className={styles.rightContent}>
                {beforeBellContent}
                <ThemeToggle />
                <NotificationBell />
                <SettingsButton onClick={() => setIsSettingsOpen(true)} />
                {rightContent}
              </div>
            </div>
          </>
        )}
      </div>

      <WorkspaceSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      {/* Loading-bar bus indicator — 2px strip at the header's bottom edge. */}
      <TopProgressBar />
    </header>
  );
});

PageHeader.displayName = 'PageHeader';

export default PageHeader;
